import { useState, useMemo } from "react";
import PageMeta from "../../components/common/PageMeta";
import { useUser } from "../../context/UserInfoProvider";
// import { LoadingSpinner } from "../../components/LoadingSpinner"; // Removed as it's not used
import RequestsTab from "../../components/common/RequestsTab"; // Assuming this can be reused
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/solid"; // For room switcher
import Alert from "../../components/ui/alert/Alert"; // Added Alert import

// --- Interfaces (can be moved to a types file later) ---
interface Room {
  id: string;
  roomNumber: string;
  suiteType: string;
  isAvailable: boolean;
  // Add other relevant room properties if needed
}

// --- Dummy Data (replace with actual data fetching) ---
const allRooms: Room[] = [
  {
    id: "101",
    roomNumber: "101",
    suiteType: "Standard King",
    isAvailable: true,
  },
  {
    id: "102",
    roomNumber: "102",
    suiteType: "Standard King",
    isAvailable: false,
  },
  {
    id: "103",
    roomNumber: "103",
    suiteType: "Standard King",
    isAvailable: true,
  },
  {
    id: "201",
    roomNumber: "201",
    suiteType: "Deluxe Queen",
    isAvailable: true,
  },
  {
    id: "202",
    roomNumber: "202",
    suiteType: "Deluxe Queen",
    isAvailable: true,
  },
  {
    id: "203",
    roomNumber: "203",
    suiteType: "Deluxe Queen",
    isAvailable: false,
  },
  { id: "301", roomNumber: "301", suiteType: "Suite", isAvailable: true },
  { id: "302", roomNumber: "302", suiteType: "Suite", isAvailable: false },
];

const suiteTypes = Array.from(new Set(allRooms.map((room) => room.suiteType)));

type ApprovalTab = "CheckIn" | "CheckOut";

export default function GuestApproval() {
  const { hotelId } = useUser();
  
  const [activeTab, setActiveTab] = useState<ApprovalTab>("CheckIn");
  const [currentSuiteTypeIndex, setCurrentSuiteTypeIndex] = useState(0);
  const [selectedRoom, setSelectedRoom] = useState<Room | null>(null);

  // Form state for Check-in
  const [guestName, setGuestName] = useState("");
  const [guestEmail, setGuestEmail] = useState("");
  const [guestPhone, setGuestPhone] = useState("");

  // Form state for Check-out
  const [checkoutRoomNumber, setCheckoutRoomNumber] = useState("");

  const currentSuiteType = suiteTypes[currentSuiteTypeIndex];
  const roomsInCurrentSuiteType = useMemo(() => {
    return allRooms.filter((room) => room.suiteType === currentSuiteType);
  }, [currentSuiteType]);

  const handleNextSuiteType = () => {
    setCurrentSuiteTypeIndex(
      (prevIndex) => (prevIndex + 1) % suiteTypes.length
    );
    setSelectedRoom(null); // Reset selection when changing suite type
  };

  const handlePrevSuiteType = () => {
    setCurrentSuiteTypeIndex(
      (prevIndex) => (prevIndex - 1 + suiteTypes.length) % suiteTypes.length
    );
    setSelectedRoom(null); // Reset selection when changing suite type
  };

  const handleRoomSelect = (room: Room) => {
    if (room.isAvailable) {
      setSelectedRoom(room);
    }
  };

  const handleCheckIn = () => {
    if (!selectedRoom || !guestName) {
      alert("Please select an available room and enter guest name."); // Placeholder for proper validation/notification
      return;
    }
    // --- TODO: Implement actual check-in logic ---
    console.log(
      "Checking in guest:",
      guestName,
      "to room:",
      selectedRoom.roomNumber,
      "Hotel ID:",
      hotelId
    );
    alert(
      `SUCCESS: ${guestName} checked into room ${selectedRoom.roomNumber}. (Placeholder Notification)`
    );
    // Reset form
    setGuestName("");
    setGuestEmail("");
    setGuestPhone("");
    setSelectedRoom(null);
    // Potentially update room availability in `allRooms` or refetch
  };

  const handleCheckOut = () => {
    if (!checkoutRoomNumber) {
      alert("Please enter the room number to check out."); // Placeholder
      return;
    }
    // --- TODO: Implement actual check-out logic ---
    console.log(
      "Checking out room:",
      checkoutRoomNumber,
      "Hotel ID:",
      hotelId
    );
    alert(
      `SUCCESS: Room ${checkoutRoomNumber} checked out. (Placeholder Notification)`
    );
    // Reset form
    setCheckoutRoomNumber("");
    // Potentially update room availability
  };

  return (
    <>
      <PageMeta
        title="Guest Check-in/Check-out - SelfServe"
        description="SelfServe Guest Check-in and Check-out Dashboard"
      />
      <div className="col-span-12 p-4 md:p-6">
        <h1 className="text-2xl font-bold text-gray-800 dark:text-white mb-6">
          Guest Management
        </h1>

        <div className="mb-6">
          <Alert
            variant="warning"
            title="WORK IN PROGRESS"
            message="This page is currently using placeholder data during the Alpha release. Real time information will be released soon."
          />
        </div>

        <div className="mb-6">
          <RequestsTab
            onFrameChange={(frame) => setActiveTab(frame as ApprovalTab)}
            selectedFrame={activeTab}
            options={["CheckIn", "CheckOut"]}
          />
        </div>

        {activeTab === "CheckIn" && (
          <div className="space-y-8">
            {/* Check-in Form */}
            <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg p-6">
              <h2 className="text-xl font-semibold mb-4 text-gray-700 dark:text-gray-200">
                Guest Information
              </h2>
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label
                    htmlFor="guestName"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                  >
                    Guest Name*
                  </label>
                  <input
                    type="text"
                    id="guestName"
                    value={guestName}
                    onChange={(e) => setGuestName(e.target.value)}
                    className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm bg-white dark:bg-gray-700 px-3 py-2"
                  />
                </div>
                <div>
                  <label
                    htmlFor="guestEmail"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                  >
                    Guest Email
                  </label>
                  <input
                    type="email"
                    id="guestEmail"
                    value={guestEmail}
                    onChange={(e) => setGuestEmail(e.target.value)}
                    className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm bg-white dark:bg-gray-700 px-3 py-2"
                  />
                </div>
                <div>
                  <label
                    htmlFor="guestPhone"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                  >
                    Guest Phone
                  </label>
                  <input
                    type="tel"
                    id="guestPhone"
                    value={guestPhone}
                    onChange={(e) => setGuestPhone(e.target.value)}
                    className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm bg-white dark:bg-gray-700 px-3 py-2"
                  />
                </div>
              </div>
            </div>

            {/* Room Selection Card */}
            <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-gray-700 dark:text-gray-200">
                  Select Room
                </h2>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={handlePrevSuiteType}
                    className="p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    <ChevronLeftIcon className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                  </button>
                  <span className="text-md font-medium text-gray-700 dark:text-gray-200">
                    {currentSuiteType}
                  </span>
                  <button
                    onClick={handleNextSuiteType}
                    className="p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    <ChevronRightIcon className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                  </button>
                </div>
              </div>

              {roomsInCurrentSuiteType.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                  {roomsInCurrentSuiteType.map((room) => (
                    <button
                      key={room.id}
                      onClick={() => handleRoomSelect(room)}
                      disabled={!room.isAvailable}
                      className={`p-4 border rounded-lg text-center transition-colors
                        ${
                          selectedRoom?.id === room.id
                            ? "ring-2 ring-blue-500 border-blue-500 bg-blue-50 dark:bg-blue-900/50"
                            : "border-gray-300 dark:border-gray-600"
                        }
                        ${
                          room.isAvailable
                            ? "hover:border-blue-400 dark:hover:border-blue-500 cursor-pointer"
                            : "bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed"
                        }
                      `}
                    >
                      <p className="font-semibold text-lg">{room.roomNumber}</p>
                      <p
                        className={`text-xs ${
                          room.isAvailable
                            ? "text-green-600 dark:text-green-400"
                            : "text-red-600 dark:text-red-400"
                        }`}
                      >
                        {room.isAvailable ? "Available" : "Occupied"}
                      </p>
                    </button>
                  ))}
                </div>
              ) : (
                <p className="text-center text-gray-500 dark:text-gray-400 py-4">
                  No rooms available for this suite type.
                </p>
              )}
              {selectedRoom && (
                <p className="mt-4 text-sm text-center text-gray-600 dark:text-gray-300">
                  Selected Room:{" "}
                  <span className="font-semibold">
                    {selectedRoom.roomNumber} ({selectedRoom.suiteType})
                  </span>
                </p>
              )}
            </div>

            <div className="flex justify-end">
              <button
                onClick={handleCheckIn}
                disabled={!selectedRoom || !guestName}
                className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
              >
                Complete Check-in
              </button>
            </div>
          </div>
        )}

        {activeTab === "CheckOut" && (
          <div className="space-y-6">
            <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg p-6">
              <h2 className="text-xl font-semibold mb-4 text-gray-700 dark:text-gray-200">
                Guest Check-out
              </h2>
              <div>
                <label
                  htmlFor="checkoutRoomNumber"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  Room Number to Check Out*
                </label>
                <input
                  type="text"
                  id="checkoutRoomNumber"
                  value={checkoutRoomNumber}
                  onChange={(e) => setCheckoutRoomNumber(e.target.value)}
                  placeholder="Enter room number"
                  className="mt-1 block w-full md:w-1/3 rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm bg-white dark:bg-gray-700 px-3 py-2"
                />
              </div>
            </div>
            <div className="flex justify-end">
              <button
                onClick={handleCheckOut}
                disabled={!checkoutRoomNumber}
                className="px-6 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50"
              >
                Complete Check-out
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
